import { useCallback, useMemo, useState } from "react";
import {
  Typography,
  Modal,
  Box,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import HelpButton from "../../../../components/Buttons/HelpButton/HelpButton";
import img from "../assets/length-to-diameter-ratio.png";
import { CalculationMethods } from "../../../../utils/helpers/tolerance/GeneralHelper";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

export default function HelpButtonContent({
  onChange,
  open,
  onClose,
  onCloseRequest,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [D, setD] = useState(0);
  const [L, setL] = useState(0);

  const methodToSet = useMemo(() => {
    if (D <= 0 || L <= 0) {
      return null;
    }

    if (D >= 5 * L) {
      return CalculationMethods.onePlane;
    } else {
      return CalculationMethods.dualPlane;
    }
  }, [D, L]);

  const handleDChange = useCallback((e) => {
    if (e.target.value >= 0) {
      setD(e.target.value);
    }
  }, []);

  const handleLChange = useCallback((e) => {
    if (e.target.value >= 0) {
      setL(e.target.value);
    }
  }, []);

  const methodLabel = useMemo(() => {
    return t(
      `pages.generalTolerance.components.calculationMethodSelector.modal.recommendedType`,
      {
        context:
          methodToSet === CalculationMethods.onePlane
            ? "onePlane"
            : "dualPlane",
      }
    );
  }, [methodToSet, t]);

  const handleSettingUp = useCallback(() => {
    onChange && onChange(methodToSet);
    onCloseRequest && onCloseRequest();
  }, [methodToSet, onChange, onCloseRequest]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6">
            {t(
              "pages.generalTolerance.components.calculationMethodSelector.modal.title"
            )}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            {t(
              "pages.generalTolerance.components.calculationMethodSelector.modal.enterParameters"
            )}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            justifyContent="center"
            gap={{ xs: 2, sm: 0 }}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                sx={{ my: 2, width: "80%" }}
                component="img"
                src={img}
                alt="length to diameter ratio"
              />
              <HelpButton>
                <Typography>
                  {t(
                    "pages.generalTolerance.components.calculationMethodSelector.modal.components.diameter.help"
                  )}
                </Typography>
                <Typography>
                  {t(
                    "pages.generalTolerance.components.calculationMethodSelector.modal.components.length.help"
                  )}
                </Typography>
              </HelpButton>
            </Stack>
            <Stack
              direction="column"
              sx={{ width: { xs: "100%", sm: "25%" } }}
              spacing={2}
            >
              <TextField
                label={t(
                  "pages.generalTolerance.components.calculationMethodSelector.modal.components.diameter.label"
                )}
                type="number"
                variant="outlined"
                value={D}
                onChange={handleDChange}
              />
              <TextField
                label={t(
                  "pages.generalTolerance.components.calculationMethodSelector.modal.components.length.label"
                )}
                type="number"
                variant="outlined"
                value={L}
                onChange={handleLChange}
                min={0}
              />
            </Stack>
          </Stack>

          {methodToSet && <Typography sx={{ mt: 2 }}>{methodLabel}</Typography>}
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            disabled={!methodToSet || disabled}
            onClick={handleSettingUp}
          >
            {t(
              "pages.generalTolerance.components.calculationMethodSelector.modal.setType",
              {
                method: methodToSet && methodLabel,
              }
            )}
          </Button>
        </Box>
      </Modal>
    </>
  );
}
